import type {
  AudienceScope,
  LcmStudioChannel,
  PersonalizationPolicy,
  TemplateBehavior,
  TemplateStrategy,
} from "../types";

/**
 * Types describing the `marketing_campaigns` row itself — the campaign's kind,
 * lifecycle status, and the shapes of its jsonb columns. Importable by both
 * backend and frontend. Generation knobs shared with the wider studio (channels,
 * templates, personalization, message plans) live in `../types`.
 */

export const MarketingCampaignTypes = {
  LegacyCampaign: "legacy_campaign",
  LegacyStandalone: "legacy_standalone",
  Project: "project",
} as const;

export type MarketingCampaignType =
  (typeof MarketingCampaignTypes)[keyof typeof MarketingCampaignTypes];

export const MarketingCampaignProjectStatuses = {
  BriefGenerating: "brief_generating",
  BriefCompleted: "brief_completed",
  BriefFailed: "brief_failed",
} as const;

export type MarketingCampaignProjectStatus =
  (typeof MarketingCampaignProjectStatuses)[keyof typeof MarketingCampaignProjectStatuses];

export const UNTITLED_CAMPAIGN_NAME = "Untitled campaign";

/**
 * Typed interface for the `config` JSONB column on `marketing_campaigns` — a
 * general-purpose per-campaign config bag. It holds the intake-time inputs that
 * do NOT have their own column: the "Additional assets" pickers plus the
 * generation/audience/template knobs. This column is the source of truth for
 * them — the skills read it through the campaign service, so a writer that
 * doesn't own the campaign's chat session can still set them.
 *
 * Distinct from `LcmCampaignConfig` (../types): that is the in-flight
 * generation-knob override bag carried on `InteractionContext.lcmConfigOverride`;
 * this is the persisted campaign-row column.
 */
export interface MarketingCampaignConfig {
  /**
   * `ad_artifacts_catalog` ids selected in the intake form's "Additional
   * assets" picker
   */
  productCatalogIds?: string[];
  /**
   * Library image ids selected in the intake form's "Additional assets"
   * picker
   */
  libraryImageIds?: string[];
  channels?: LcmStudioChannel[];
  audienceScope?: AudienceScope;
  personalization?: PersonalizationPolicy;
  templateBehavior?: TemplateBehavior;
  templateGuidance?: string;
  templateIds?: string[];
  componentIds?: string[];
  candidatesPerMessage?: number;
  templateStrategy?: TemplateStrategy;
  candidateSplitPlan?: string;
}

export const ExternalSourceTypes = {
  Asana: "asana",
} as const;

export type ExternalSourceType =
  (typeof ExternalSourceTypes)[keyof typeof ExternalSourceTypes];

/**
 * Typed interface for the `external_source` JSONB column on
 * `marketing_campaigns`: the record in a third-party system this campaign is
 * linked to. NULL for campaigns with no such link.
 */
export type ExternalSource = {
  type: typeof ExternalSourceTypes.Asana;
  /** Asana task gid. */
  externalId: string;
  workspaceGid: string;
  /**
   * Absent for a task that belongs to no project (a My Tasks-only or private
   * task). Recorded when available, but nothing depends on it: both the
   * reverse lookup and write-back address the task directly by `externalId`.
   */
  projectGid?: string;
  /** Deep link to the task, so rendering it needs no Asana round trip. */
  permalinkUrl: string;
};

/**
 * The stored permalink is rendered as an `<a href>` and passed to
 * `window.open`, so it has to be a real Asana URL and not, say, a
 * `javascript:` scheme
 */
function isAsanaPermalink(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "app.asana.com";
  } catch {
    return false;
  }
}

/**
 * Narrows a raw `external_source` JSONB value read from the database
 */
export function parseExternalSource(value: unknown): ExternalSource | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const candidate = value as Record<string, unknown>;

  if (candidate.type !== ExternalSourceTypes.Asana) {
    return null;
  }

  const isPresent = (field: unknown): field is string =>
    typeof field === "string" && field.length > 0;

  const { externalId, projectGid, workspaceGid, permalinkUrl } = candidate;

  if (
    !isPresent(externalId) ||
    !isPresent(workspaceGid) ||
    !isPresent(permalinkUrl) ||
    !isAsanaPermalink(permalinkUrl)
  ) {
    return null;
  }

  return {
    type: ExternalSourceTypes.Asana,
    externalId,
    workspaceGid,
    ...(isPresent(projectGid) ? { projectGid } : {}),
    permalinkUrl,
  };
}
